import {
  AspectRatio,
  MediaQuery,
  ResponsiveImageSpec,
  generateImageRenditions,
  type ImageRendition,
} from '../../utils/ImageUtils';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { OCardLayeredStandardAspectRatio } from './OCardLayeredStandard.types';

export type AspectRatioCss = `${number} / ${number}`;

interface ResponsiveAspectRatioConfig {
  mobile: AspectRatioCss;
  desktop: AspectRatioCss;
  mobileImage: AspectRatio;
  desktopImage: AspectRatio;
}

/**
 * AEM aspectRatio selection → viewport-specific crop ratios.
 * Mobile: max-width 767px (MAX_WIDTH_SM). Desktop: min-width 768px (MIN_WIDTH_MD).
 */
const ASPECT_RATIO_CONFIG: Record<OCardLayeredStandardAspectRatio, ResponsiveAspectRatioConfig> = {
  [OCardLayeredStandardAspectRatio.SQUARE_1_1]: {
    mobile: '2 / 3',
    desktop: '1 / 1',
    mobileImage: AspectRatio.CLASSIC_VER,
    desktopImage: AspectRatio.SQUARE,
  },
  [OCardLayeredStandardAspectRatio.WIDE_16_9]: {
    mobile: '2 / 3',
    desktop: '16 / 9',
    mobileImage: AspectRatio.CLASSIC_VER,
    desktopImage: AspectRatio.WIDE_HOR,
  },
  [OCardLayeredStandardAspectRatio.WIDE_3_2]: {
    mobile: '1 / 1',
    desktop: '3 / 1',
    mobileImage: AspectRatio.SQUARE,
    desktopImage: AspectRatio.PANO_HOR,
  },
  [OCardLayeredStandardAspectRatio.TALL_2_3]: {
    mobile: '2 / 3',
    desktop: '2 / 3',
    mobileImage: AspectRatio.CLASSIC_VER,
    desktopImage: AspectRatio.CLASSIC_VER,
  },
  [OCardLayeredStandardAspectRatio.TALL_9_16]: {
    mobile: '9 / 16',
    desktop: '9 / 16',
    mobileImage: AspectRatio.WIDE_VER,
    desktopImage: AspectRatio.WIDE_VER,
  },
  [OCardLayeredStandardAspectRatio.TALL_1_2]: {
    mobile: '1 / 2',
    desktop: '1 / 2',
    mobileImage: AspectRatio.STANDARD_VER,
    desktopImage: AspectRatio.STANDARD_VER,
  },
};

export function resolveAspectRatio(
  aspectRatio?: OCardLayeredStandardAspectRatio | string
): OCardLayeredStandardAspectRatio {
  const values = Object.values(OCardLayeredStandardAspectRatio);
  if (aspectRatio && values.includes(aspectRatio as OCardLayeredStandardAspectRatio)) {
    return aspectRatio as OCardLayeredStandardAspectRatio;
  }
  return OCardLayeredStandardAspectRatio.WIDE_16_9;
}

export function getResponsiveAspectRatioConfig(
  aspectRatio?: OCardLayeredStandardAspectRatio | string
): ResponsiveAspectRatioConfig {
  return ASPECT_RATIO_CONFIG[resolveAspectRatio(aspectRatio)];
}

export interface TextClampConfig {
  /** Eyebrow: single-line truncation (all variations). */
  eyebrowLines: number;
  headlineLines: number;
  bodyLines: number;
}

/**
 * Line-clamp limits per aspectRatio variation (content spec).
 * Character guidance: eyebrow ≤30, headline ≤90, body ≤60 — enforced visually via ellipsis.
 */
const TEXT_CLAMP_CONFIG: Record<OCardLayeredStandardAspectRatio, TextClampConfig> = {
  [OCardLayeredStandardAspectRatio.TALL_1_2]: {
    eyebrowLines: 1,
    headlineLines: 3,
    bodyLines: 4,
  },
  [OCardLayeredStandardAspectRatio.TALL_9_16]: {
    eyebrowLines: 1,
    headlineLines: 3,
    bodyLines: 3,
  },
  [OCardLayeredStandardAspectRatio.TALL_2_3]: {
    eyebrowLines: 1,
    headlineLines: 3,
    bodyLines: 3,
  },
  [OCardLayeredStandardAspectRatio.SQUARE_1_1]: {
    eyebrowLines: 1,
    headlineLines: 2,
    bodyLines: 2,
  },
  [OCardLayeredStandardAspectRatio.WIDE_3_2]: {
    eyebrowLines: 1,
    headlineLines: 1,
    bodyLines: 2,
  },
  [OCardLayeredStandardAspectRatio.WIDE_16_9]: {
    eyebrowLines: 1,
    headlineLines: 1,
    bodyLines: 1,
  },
};

export function getTextClampConfig(aspectRatio?: OCardLayeredStandardAspectRatio | string): TextClampConfig {
  return TEXT_CLAMP_CONFIG[resolveAspectRatio(aspectRatio)];
}

export function getEllipsisClassName(lines: number): string {
  return `m-ellipsis-${lines}`;
}

/** Logo is not supported on wide, short card crops. */
export function isLogoEnabledForAspectRatio(aspectRatio?: OCardLayeredStandardAspectRatio | string): boolean {
  const resolved = resolveAspectRatio(aspectRatio);
  return (
    resolved !== OCardLayeredStandardAspectRatio.WIDE_3_2 && resolved !== OCardLayeredStandardAspectRatio.WIDE_16_9
  );
}

const WIDE_ASPECT_RATIOS_WITHOUT_XL_SUBHEADER = new Set([
  OCardLayeredStandardAspectRatio.WIDE_3_2,
  OCardLayeredStandardAspectRatio.WIDE_16_9,
]);

/** Figma: 3:2 and 16:9 do not support XL subheader. */
export function isXlSubheaderEnabledForAspectRatio(aspectRatio?: OCardLayeredStandardAspectRatio | string): boolean {
  return !WIDE_ASPECT_RATIOS_WITHOUT_XL_SUBHEADER.has(resolveAspectRatio(aspectRatio));
}

/**
 * Resolves authored subheader size for the card crop.
 * Wide 3:2 and 16:9 cap at L (including when size is omitted, since MTextBlockBasic defaults to XL).
 */
export function resolveSubheaderSizeForAspectRatio(
  aspectRatio?: OCardLayeredStandardAspectRatio | string,
  size?: ASubheaderSize | string
): ASubheaderSize | undefined {
  if (isXlSubheaderEnabledForAspectRatio(aspectRatio)) {
    return size as ASubheaderSize | undefined;
  }

  const isXl = size === ASubheaderSize.XL || size === 'XL';
  if (!size || isXl) {
    return ASubheaderSize.L;
  }

  return size as ASubheaderSize;
}

export interface LogoConfig {
  brand?: string;
  logopath?: string;
  color?: string;
}

export interface ButtonIconConfig {
  aIcon?: { icon?: string };
}

/** Top-right icon button renders only when an icon name is authored. */
export function shouldShowButtonIcon(buttonIconConfig?: ButtonIconConfig | null): boolean {
  return Boolean(buttonIconConfig?.aIcon?.icon?.trim());
}

/** Logo renders only when authored and the aspect ratio allows it. */
export function shouldShowLogo(
  aspectRatio?: OCardLayeredStandardAspectRatio | string,
  logoConfig?: LogoConfig | null
): boolean {
  if (!isLogoEnabledForAspectRatio(aspectRatio)) {
    return false;
  }

  const brand = logoConfig?.brand?.trim();
  const logopath = logoConfig?.logopath?.trim();
  return Boolean(brand || logopath);
}

export function getResponsiveImageSpecs(aspectRatio?: OCardLayeredStandardAspectRatio | string): ResponsiveImageSpec[] {
  const { mobileImage, desktopImage } = getResponsiveAspectRatioConfig(aspectRatio);

  return [
    {
      media: MediaQuery.MAX_WIDTH_SM,
      aspectRatio: mobileImage,
      imgWidthPx: 767,
    },
    {
      media: MediaQuery.MIN_WIDTH_MD,
      aspectRatio: desktopImage,
      imgWidthPx: 1200,
    },
  ];
}

/**
 * Builds a Scene7 path using wid/hei for a 1:2 crop.
 * Standard-Ver smart crop is unavailable for many assets (403); height = 2× width.
 */
export function buildOneToTwoRenditionPath(assetPath: string, widthPx: number, fit = 'constrain'): string {
  const heightPx = widthPx * 2;
  const path1 = `${assetPath}?wid=${widthPx}&hei=${heightPx}&fit=${fit}`;
  const path2 = `${assetPath}?wid=${widthPx * 2}&hei=${heightPx * 2}&fit=${fit} 2x`;
  return [path1, path2].join(', ');
}

/** Responsive picture sources; 1:2 uses wid/hei instead of Standard-Ver smart crop. */
export function getImageRenditionsForAspectRatio(
  aspectRatio: OCardLayeredStandardAspectRatio | string,
  assetPath: string
): ImageRendition[] {
  const resolved = resolveAspectRatio(aspectRatio);

  if (resolved === OCardLayeredStandardAspectRatio.TALL_1_2) {
    return [
      {
        media: MediaQuery.MAX_WIDTH_SM,
        aspectRatio: AspectRatio.STANDARD_VER,
        smartCropType: null,
        renditionPath: buildOneToTwoRenditionPath(assetPath, 767),
      },
      {
        media: MediaQuery.MIN_WIDTH_MD,
        aspectRatio: AspectRatio.STANDARD_VER,
        smartCropType: null,
        renditionPath: buildOneToTwoRenditionPath(assetPath, 1200),
      },
    ];
  }

  return generateImageRenditions(getResponsiveImageSpecs(resolved), assetPath);
}
